import { Head, Link, router } from '@inertiajs/react'
import { Plus, Edit, Trash2, Users, ArrowLeft } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
  online: boolean
}

interface CurrentAgent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface AgentsIndexProps {
  agents: Agent[]
  currentAgent: CurrentAgent
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getAvatarColor(name: string): string {
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-pink-500',
  ]
  const index = name.charCodeAt(0) % colors.length
  return colors[index]
}

export default function AgentsIndex({ agents, currentAgent }: AgentsIndexProps) {
  const handleDelete = (id: number, name: string) => {
    if (confirm(`Tem certeza que deseja excluir "${name}"?`)) {
      router.delete(`/agents/${id}`)
    }
  }

  return (
    <>
      <Head title="Equipe" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6">
            <div className="flex items-center">
              <Link
                href="/inbox"
                className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
            </div>
            <Link
              href="/agents/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Novo Agente
            </Link>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="agents" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-3xl">
                {agents.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum agente ainda</h3>
                    <p className="text-gray-500 mb-4">
                      Adicione agentes para sua equipe de atendimento
                    </p>
                    <Link
                      href="/agents/new"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Criar primeiro agente
                    </Link>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                    {agents.map((agent) => {
                      const isSelf = agent.id === currentAgent.id

                      return (
                        <div key={agent.id} className="p-4 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="relative">
                              {agent.avatar_url ? (
                                <img
                                  src={agent.avatar_url}
                                  alt={agent.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                                    agent.name
                                  )}`}
                                >
                                  {getInitials(agent.name)}
                                </div>
                              )}
                              {/* Online indicator */}
                              <span
                                className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                                  agent.online ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 truncate">
                                  {agent.name}
                                </span>
                                {agent.role === 'admin' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                    Admin
                                  </span>
                                )}
                                {isSelf && (
                                  <span className="text-xs text-gray-400">(você)</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 truncate">{agent.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Link
                              href={`/agents/${agent.id}/edit`}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => handleDelete(agent.id, agent.name)}
                              disabled={isSelf}
                              className={`p-2 rounded-lg transition-colors ${
                                isSelf
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title={isSelf ? 'Você não pode excluir sua própria conta' : 'Excluir'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
